import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, Keyboard, X } from "@phosphor-icons/react";

const REGION_ID = "barcode-scanner-region";

export default function BarcodeScannerDialog({ open, onOpenChange, onDetected }) {
  const [mode, setMode] = useState("camera"); // camera | manual
  const [manual, setManual] = useState("");
  const [error, setError] = useState("");
  const scannerRef = useRef(null);

  useEffect(() => {
    if (!open || mode !== "camera") return;
    let cancelled = false;
    setError("");
    const start = async () => {
      try {
        const scanner = new Html5Qrcode(REGION_ID, { verbose: false });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 160 } },
          (decodedText) => {
            if (cancelled) return;
            cancelled = true;
            scanner.stop().then(() => scanner.clear()).catch(() => {});
            onDetected(decodedText);
          },
          () => { /* ignore per-frame errors */ }
        );
      } catch (e) {
        setError("Impossibile accedere alla fotocamera. Consenti l'accesso o usa l'inserimento manuale.");
      }
    };
    start();
    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (s) {
        s.stop().then(() => s.clear()).catch(() => {});
        scannerRef.current = null;
      }
    };
  }, [open, mode, onDetected]);

  const submitManual = (e) => {
    e?.preventDefault?.();
    const val = manual.trim();
    if (val) { onDetected(val); setManual(""); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="barcode-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Scansiona barcode</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 mb-2">
          <button type="button" onClick={() => setMode("camera")} data-testid="scan-mode-camera"
            className={`text-sm py-2 rounded-md border ${mode === "camera" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white text-slate-700"}`}>
            <Camera size={14} weight="bold" className="inline mr-1.5"/> Fotocamera
          </button>
          <button type="button" onClick={() => setMode("manual")} data-testid="scan-mode-manual"
            className={`text-sm py-2 rounded-md border ${mode === "manual" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white text-slate-700"}`}>
            <Keyboard size={14} weight="bold" className="inline mr-1.5"/> Inserimento
          </button>
        </div>

        {mode === "camera" ? (
          <div>
            <div id={REGION_ID} className="w-full aspect-[4/3] bg-slate-900 rounded-md overflow-hidden" data-testid="scanner-region"/>
            {error && <div className="mt-2 text-xs text-red-600" data-testid="scanner-error">{error}</div>}
            <div className="mt-2 text-xs text-slate-500">Inquadra il codice a barre o QR del prodotto.</div>
          </div>
        ) : (
          <form onSubmit={submitManual} className="space-y-3">
            <Input autoFocus placeholder="Digita o incolla il codice…" value={manual}
              onChange={(e) => setManual(e.target.value)} data-testid="scan-manual-input" className="h-11"/>
            <Button type="submit" className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white" data-testid="scan-manual-submit">
              Cerca prodotto
            </Button>
          </form>
        )}

        <button type="button" onClick={() => onOpenChange(false)}
          className="mt-2 text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
          data-testid="scanner-close">
          <X size={12}/> Chiudi
        </button>
      </DialogContent>
    </Dialog>
  );
}
